import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import Webcam from 'react-webcam';
import SignatureCanvas from 'react-signature-canvas';
import { CreditCard, Plus, Minus, Camera, Pen, FileText, Search, AlertTriangle, Check, X, Phone, User, Euro, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { api } from '../services/api';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Infos créancier pour le PDF
const CREDITOR_INFO = {
  name: "La Petite Épicerie",
  owner: "VM Market",
  address: "138 Rue Arthur Lamendin, 62980 Vermelles",
  siret: "944 108 638"
};

export default function CreditManagement({ vendorCode, vendorName }) {
  // États principaux
  const [activeView, setActiveView] = useState('list'); // 'list', 'new', 'details'
  const [credits, setCredits] = useState({ clients: [], stats: {} });
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // États pour nouveau crédit
  const [newCreditName, setNewCreditName] = useState('');
  const [newCreditPhone, setNewCreditPhone] = useState('');
  const [newCreditAmount, setNewCreditAmount] = useState('');
  const [newCreditDescription, setNewCreditDescription] = useState('');
  
  // États pour client sélectionné
  const [selectedClient, setSelectedClient] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [showTransactions, setShowTransactions] = useState(false);
  
  // États pour versement
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDescription, setPaymentDescription] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  // États pour capture photo ID
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [pendingCreditData, setPendingCreditData] = useState(null);
  const webcamRef = useRef(null);
  
  // États pour signature
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureClientId, setSignatureClientId] = useState(null);
  const [debtorAddress, setDebtorAddress] = useState('');
  const [repaymentConditions, setRepaymentConditions] = useState('Remboursement en une ou plusieurs fois selon les possibilités du débiteur');
  const [repaymentDeadline, setRepaymentDeadline] = useState('');
  const signatureRef = useRef(null);
  
  // États pour génération PDF
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const pdfRef = useRef(null);

  // Charger les crédits
  useEffect(() => {
    loadCredits();
  }, []);

  const loadCredits = async () => {
    try {
      const data = await api.getCredits();
      setCredits(data);
    } catch (error) {
      console.error('Erreur chargement crédits:', error);
      toast.error('Erreur lors du chargement des crédits');
    }
  };

  // Rechercher un client
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadCredits();
      return;
    }
    try {
      const results = await api.searchCreditClient(searchQuery);
      setCredits(prev => ({ ...prev, clients: results }));
    } catch (error) {
      toast.error('Erreur de recherche');
    }
  };

  // Voir détails d'un client
  const handleViewClient = async (clientId) => {
    setIsLoading(true);
    try {
      const data = await api.getCreditClient(clientId);
      setSelectedClient(data.client);
      setTransactions(data.transactions);
      setActiveView('details');
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  // Créer un nouveau crédit
  const handleCreateCredit = async () => {
    if (!newCreditName.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    if (!newCreditPhone.trim()) {
      toast.error('Le téléphone est obligatoire pour les crédits');
      return;
    }
    if (!newCreditAmount || parseFloat(newCreditAmount) <= 0) {
      toast.error('Montant invalide');
      return;
    }

    setIsLoading(true);
    try {
      const result = await api.createCredit({
        name: newCreditName,
        phone: newCreditPhone,
        initialCredit: parseFloat(newCreditAmount),
        notes: newCreditDescription || 'Achat à crédit'
      });

      if (result.success) {
        toast.success(`Crédit de ${newCreditAmount}€ enregistré pour ${newCreditName}`);
        resetNewCreditForm();
        loadCredits();
        setActiveView('list');
      } else if (result.requiresIdPhoto) {
        // Palier 100€ atteint - demander photo ID
        setPendingCreditData({
          clientId: result.clientId,
          name: newCreditName,
          phone: newCreditPhone,
          amount: parseFloat(newCreditAmount),
          description: newCreditDescription
        });
        setShowCameraModal(true);
        toast.info('📷 Photo de pièce d\'identité requise pour dépasser 100€');
      } else if (result.requiresSignature) {
        // Palier 200€ atteint - demander signature
        setSignatureClientId(result.clientId);
        setShowSignatureModal(true);
        toast.info('✍️ Signature et reconnaissance de dette requises pour dépasser 200€');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du crédit');
    } finally {
      setIsLoading(false);
    }
  };

  // Capturer photo pièce d'identité
  const capturePhoto = useCallback(async () => {
    if (!webcamRef.current) return;
    
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      toast.error('Erreur de capture');
      return;
    }

    setIsLoading(true);
    try {
      // Upload la photo
      await api.uploadCreditId(pendingCreditData.clientId, imageSrc);
      toast.success('Photo enregistrée !');
      
      // Maintenant créer le crédit
      const result = await api.createCredit({
        name: pendingCreditData.name,
        phone: pendingCreditData.phone,
        initialCredit: pendingCreditData.amount,
        notes: pendingCreditData.description
      });

      if (result.success) {
        toast.success(`Crédit de ${pendingCreditData.amount}€ enregistré !`);
        setShowCameraModal(false);
        setPendingCreditData(null);
        resetNewCreditForm();
        loadCredits();
        setActiveView('list');
      } else if (result.requiresSignature) {
        setShowCameraModal(false);
        setSignatureClientId(result.clientId);
        setShowSignatureModal(true);
        toast.info('✍️ Signature requise pour dépasser 200€');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement de la photo');
    } finally {
      setIsLoading(false);
    }
  }, [pendingCreditData, vendorCode]);

  // Enregistrer signature et générer reconnaissance de dette
  const handleSignature = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error('Veuillez signer');
      return;
    }
    if (!debtorAddress.trim()) {
      toast.error('L\'adresse du débiteur est obligatoire');
      return;
    }

    setIsLoading(true);
    try {
      const signatureData = signatureRef.current.toDataURL();
      
      // Upload signature
      await api.uploadCreditSignature(signatureClientId, signatureData);
      
      // Récupérer les données du client pour le PDF
      const clientData = await api.getCreditClient(signatureClientId);
      
      // Préparer les données pour le PDF
      setPdfData({
        client: clientData.client,
        signature: signatureData,
        address: debtorAddress,
        conditions: repaymentConditions,
        deadline: repaymentDeadline,
        date: new Date().toLocaleDateString('fr-FR')
      });
      
      setShowSignatureModal(false);
      setShowPdfPreview(true);
      
      // Finaliser le crédit si en attente
      if (pendingCreditData) {
        const result = await api.createCredit({
          name: pendingCreditData.name,
          phone: pendingCreditData.phone,
          amount: pendingCreditData.amount,
          description: pendingCreditData.description,
          vendorCode: vendorCode
        });
        
        if (result.success) {
          toast.success(`Crédit de ${pendingCreditData.amount}€ enregistré !`);
          setPendingCreditData(null);
          resetNewCreditForm();
        }
      }
      
      loadCredits();
      toast.success('Signature enregistrée, reconnaissance de dette créée !');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement de la signature');
    } finally {
      setIsLoading(false);
    }
  };

  // Générer et télécharger le PDF
  const generatePDF = async () => {
    if (!pdfData || !pdfRef.current) return;
    
    try {
      const canvas = await html2canvas(pdfRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`reconnaissance_dette_${pdfData.client.name.replace(/\s/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
      
      toast.success('PDF téléchargé !');
    } catch (error) {
      console.error('Erreur PDF:', error);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  // Ajouter un versement
  const handlePayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Montant invalide');
      return;
    }

    setIsLoading(true);
    try {
      const result = await api.addCreditPayment(selectedClient.id, {
        amount: parseFloat(paymentAmount),
        notes: paymentDescription || 'Versement',
        method: 'cash'
      });

      if (result.newBalance === 0) {
        toast.success('🎉 Dette soldée ! Client à jour.');
      } else {
        toast.success(`Versement de ${paymentAmount}€ enregistré. Reste: ${result.newBalance.toFixed(2)}€`);
      }
      
      setShowPaymentModal(false);
      setPaymentAmount('');
      setPaymentDescription('');
      
      // Recharger les données du client
      await handleViewClient(selectedClient.id);
      loadCredits();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement du versement');
    } finally {
      setIsLoading(false);
    }
  };

  const resetNewCreditForm = () => {
    setNewCreditName('');
    setNewCreditPhone('');
    setNewCreditAmount('');
    setNewCreditDescription('');
  };

  // Filtrer les clients par recherche
  const filteredClients = credits.clients?.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.phone?.includes(searchQuery)
  ) || [];

  return (
    <div className="space-y-6">
      {/* Header avec stats */}
      <div 
        className="backdrop-blur-xl rounded-3xl p-6 border-2"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 0, 0, 0.1) 0%, rgba(10, 0, 30, 0.9) 50%, rgba(255, 100, 0, 0.1) 100%)',
          borderColor: '#FF4444',
          boxShadow: '0 0 40px rgba(255, 68, 68, 0.4)'
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <CreditCard className="w-7 h-7 text-red-400" />
            💳 Gestion Crédits
          </h2>
          <Button
            onClick={() => setActiveView(activeView === 'new' ? 'list' : 'new')}
            style={{
              background: activeView === 'new' 
                ? 'linear-gradient(135deg, #666 0%, #888 100%)' 
                : 'linear-gradient(135deg, #FF4444 0%, #FF8800 100%)'
            }}
          >
            {activeView === 'new' ? (
              <>
                <X className="w-5 h-5 mr-2" />
                Annuler
              </>
            ) : (
              <>
                <Plus className="w-5 h-5 mr-2" />
                Nouveau crédit
              </>
            )}
          </Button>
        </div>

        {/* Stats rapides */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-black/50 rounded-xl p-3 text-center border border-red-500/50">
            <div className="text-2xl font-bold text-red-400">{(credits.stats?.totalDebt || 0).toFixed(0)}€</div>
            <div className="text-xs text-gray-400">Total dû</div>
          </div>
          <div className="bg-black/50 rounded-xl p-3 text-center border border-orange-500/50">
            <div className="text-2xl font-bold text-orange-400">{credits.stats?.activeDebtors || 0}</div>
            <div className="text-xs text-gray-400">Débiteurs</div>
          </div>
          <div className="bg-black/50 rounded-xl p-3 text-center border border-yellow-500/50">
            <div className="text-2xl font-bold text-yellow-400">{credits.stats?.alert100 || 0}</div>
            <div className="text-xs text-gray-400">100€-200€</div>
          </div>
          <div className="bg-black/50 rounded-xl p-3 text-center border border-red-600/50">
            <div className="text-2xl font-bold text-red-500">{credits.stats?.alert200 || 0}</div>
            <div className="text-xs text-gray-400">+200€ ⚠️</div>
          </div>
        </div>
      </div>

      {/* Vue liste des clients */}
      {activeView === 'list' && (
        <div 
          className="backdrop-blur-xl rounded-3xl p-6 border-2"
          style={{
            background: 'rgba(10, 0, 30, 0.7)',
            borderColor: '#FF6B00',
            boxShadow: '0 0 30px rgba(255, 107, 0, 0.3)'
          }}
        >
          {/* Barre de recherche */}
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Rechercher par nom ou téléphone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 bg-black/50 border-gray-600 text-white"
              />
            </div>
            <Button onClick={handleSearch} variant="outline" className="border-gray-600">
              <Search className="w-5 h-5" />
            </Button>
          </div>

          {/* Liste des débiteurs */}
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {filteredClients.length === 0 ? (
              <p className="text-gray-400 text-center py-8">
                {searchQuery ? 'Aucun résultat' : 'Aucun crédit en cours 🎉'}
              </p>
            ) : (
              filteredClients.filter(c => c.currentBalance > 0).map((client) => (
                <motion.div 
                  key={client.id}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all hover:scale-[1.01] ${
                    client.currentBalance >= 200 
                      ? 'bg-red-500/20 border-red-500' 
                      : client.currentBalance >= 100 
                        ? 'bg-yellow-500/20 border-yellow-500'
                        : 'bg-black/30 border-gray-700'
                  }`}
                  onClick={() => handleViewClient(client.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                        client.currentBalance >= 200 ? 'bg-red-500' : client.currentBalance >= 100 ? 'bg-yellow-500' : 'bg-gray-600'
                      }`}>
                        {client.currentBalance >= 200 ? '🔴' : client.currentBalance >= 100 ? '🟡' : '🟢'}
                      </div>
                      <div>
                        <div className="font-bold text-white">{client.name}</div>
                        {client.phone && <div className="text-sm text-gray-400">📱 {client.phone}</div>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-2xl font-bold ${
                        client.currentBalance >= 200 ? 'text-red-400' : client.currentBalance >= 100 ? 'text-yellow-400' : 'text-orange-400'
                      }`}>
                        {client.currentBalance.toFixed(2)}€
                      </div>
                      <div className="flex gap-1 mt-1">
                        {client.idPhotoUrl && <span className="text-xs text-green-400">📷</span>}
                        {client.signatureUrl && <span className="text-xs text-green-400">✍️</span>}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Vue nouveau crédit */}
      {activeView === 'new' && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="backdrop-blur-xl rounded-3xl p-6 border-2"
          style={{
            background: 'rgba(10, 0, 30, 0.7)',
            borderColor: '#00FF00',
            boxShadow: '0 0 30px rgba(0, 255, 0, 0.3)'
          }}
        >
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Plus className="w-6 h-6 text-green-400" />
            Nouveau Crédit
          </h3>

          <div className="space-y-4">
            <div>
              <Label className="text-white flex items-center gap-2">
                <User className="w-4 h-4" /> Nom du client *
              </Label>
              <Input
                placeholder="Ex: Jean Dupont"
                value={newCreditName}
                onChange={(e) => setNewCreditName(e.target.value)}
                className="mt-2 bg-black/50 border-green-500 text-white"
              />
            </div>

            <div>
              <Label className="text-white flex items-center gap-2">
                <Phone className="w-4 h-4" /> Téléphone *
              </Label>
              <Input
                type="tel"
                placeholder="Ex: 0612345678"
                value={newCreditPhone}
                onChange={(e) => setNewCreditPhone(e.target.value)}
                className="mt-2 bg-black/50 border-green-500 text-white"
              />
              <p className="text-xs text-gray-400 mt-1">Obligatoire pour relancer le client</p>
            </div>

            <div>
              <Label className="text-white flex items-center gap-2">
                <Euro className="w-4 h-4" /> Montant (€) *
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="Ex: 25.50"
                value={newCreditAmount}
                onChange={(e) => setNewCreditAmount(e.target.value)}
                className="mt-2 bg-black/50 border-green-500 text-white text-xl font-bold"
              />
            </div>

            <div>
              <Label className="text-white">Description (optionnel)</Label>
              <Input
                placeholder="Ex: Courses du 15/12"
                value={newCreditDescription}
                onChange={(e) => setNewCreditDescription(e.target.value)}
                className="mt-2 bg-black/50 border-gray-600 text-white"
              />
            </div>

            {/* Alerte paliers */}
            {parseFloat(newCreditAmount) >= 100 && (
              <div className={`p-3 rounded-xl border-2 ${
                parseFloat(newCreditAmount) >= 200 
                  ? 'bg-red-500/20 border-red-500' 
                  : 'bg-yellow-500/20 border-yellow-500'
              }`}>
                <div className="flex items-center gap-2">
                  <AlertTriangle className={`w-5 h-5 ${parseFloat(newCreditAmount) >= 200 ? 'text-red-400' : 'text-yellow-400'}`} />
                  <span className={parseFloat(newCreditAmount) >= 200 ? 'text-red-400' : 'text-yellow-400'}>
                    {parseFloat(newCreditAmount) >= 200 
                      ? '⚠️ Signature et reconnaissance de dette requises' 
                      : '📷 Photo de pièce d\'identité requise'}
                  </span>
                </div>
              </div>
            )}

            <Button
              onClick={handleCreateCredit}
              disabled={isLoading || !newCreditName.trim() || !newCreditPhone.trim() || !newCreditAmount}
              className="w-full py-4 text-lg font-bold"
              style={{
                background: 'linear-gradient(135deg, #00FF00 0%, #00CC00 100%)',
                color: '#000'
              }}
            >
              {isLoading ? 'Enregistrement...' : '✅ Enregistrer le crédit'}
            </Button>
          </div>
        </motion.div>
      )}

      {/* Vue détails client */}
      {activeView === 'details' && selectedClient && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="backdrop-blur-xl rounded-3xl p-6 border-2"
          style={{
            background: 'rgba(10, 0, 30, 0.7)',
            borderColor: '#8B5CF6',
            boxShadow: '0 0 30px rgba(139, 92, 246, 0.3)'
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              onClick={() => { setActiveView('list'); setSelectedClient(null); }}
              className="text-gray-400"
            >
              ← Retour
            </Button>
            <h3 className="text-xl font-bold text-white">{selectedClient.name}</h3>
            <div />
          </div>

          {/* Infos client */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-black/50 rounded-xl p-4 border border-purple-500/50">
              <div className="text-sm text-gray-400 mb-1">Solde actuel</div>
              <div className={`text-3xl font-bold ${
                selectedClient.currentBalance >= 200 ? 'text-red-400' : selectedClient.currentBalance >= 100 ? 'text-yellow-400' : selectedClient.currentBalance > 0 ? 'text-orange-400' : 'text-green-400'
              }`}>
                {selectedClient.currentBalance.toFixed(2)}€
              </div>
            </div>
            <div className="bg-black/50 rounded-xl p-4 border border-gray-600">
              <div className="text-sm text-gray-400 mb-1">Total crédité</div>
              <div className="text-2xl font-bold text-white">{(selectedClient.totalCreditGiven || 0).toFixed(2)}€</div>
            </div>
          </div>

          {/* Contact */}
          {selectedClient.phone && (
            <div className="bg-black/50 rounded-xl p-3 mb-4 flex items-center gap-3">
              <Phone className="w-5 h-5 text-cyan-400" />
              <a href={`tel:${selectedClient.phone}`} className="text-cyan-400 hover:underline">
                {selectedClient.phone}
              </a>
            </div>
          )}

          {/* Documents */}
          <div className="flex gap-2 mb-6">
            {selectedClient.idPhotoUrl ? (
              <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                <Camera className="w-4 h-4" /> Pièce ID ✓
              </span>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="border-yellow-500 text-yellow-400"
                onClick={() => {
                  setPendingCreditData({ clientId: selectedClient.id });
                  setShowCameraModal(true);
                }}
              >
                <Camera className="w-4 h-4 mr-1" /> Ajouter pièce ID
              </Button>
            )}
            {selectedClient.signatureUrl ? (
              <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                <Pen className="w-4 h-4" /> Signé ✓
              </span>
            ) : selectedClient.currentBalance >= 200 && (
              <Button
                size="sm"
                variant="outline"
                className="border-red-500 text-red-400"
                onClick={() => {
                  setSignatureClientId(selectedClient.id);
                  setShowSignatureModal(true);
                }}
              >
                <Pen className="w-4 h-4 mr-1" /> Faire signer
              </Button>
            )}
          </div>

          {/* Actions */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <Button
              onClick={() => setShowPaymentModal(true)}
              className="py-4 font-semibold"
              style={{
                background: 'linear-gradient(135deg, #00FF00 0%, #00CC00 100%)',
                color: '#000'
              }}
            >
              <Minus className="w-5 h-5 mr-2" />
              Versement
            </Button>
            <Button
              onClick={() => {
                setNewCreditName(selectedClient.name);
                setNewCreditPhone(selectedClient.phone || '');
                setActiveView('new');
              }}
              style={{
                background: 'linear-gradient(135deg, #FF4444 0%, #FF8800 100%)'
              }}
            >
              <Plus className="w-5 h-5 mr-2" />
              Ajouter crédit
            </Button>
          </div>

          {/* Historique */}
          <div 
            className="bg-black/30 rounded-xl p-4 cursor-pointer"
            onClick={() => setShowTransactions(!showTransactions)}
          >
            <div className="flex items-center justify-between">
              <span className="font-semibold text-white">📜 Historique ({transactions.length})</span>
              {showTransactions ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </div>
          </div>

          {showTransactions && (
            <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
              {transactions.map((trans) => (
                <div 
                  key={trans.id}
                  className={`p-3 rounded-lg flex items-center justify-between ${
                    trans.type === 'payment' ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
                  }`}
                >
                  <div>
                    <div className={`font-semibold ${trans.type === 'payment' ? 'text-green-400' : 'text-red-400'}`}>
                      {trans.type === 'payment' ? '💵 Versement' : '🛒 Crédit'}
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(trans.createdAt).toLocaleDateString('fr-FR')} - {trans.description || '-'}
                    </div>
                  </div>
                  <div className={`text-xl font-bold ${trans.type === 'payment' ? 'text-green-400' : 'text-red-400'}`}>
                    {trans.type === 'payment' ? '-' : '+'}{trans.amount.toFixed(2)}€
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Modal Capture Photo ID */}
      <Dialog open={showCameraModal} onOpenChange={setShowCameraModal}>
        <DialogContent className="sm:max-w-lg bg-black/95 border-2 border-yellow-500">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white text-center">
              📷 Photo Pièce d&apos;Identité
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-center text-yellow-400">
              Photographiez la pièce d&apos;identité du client
            </p>
            <div className="relative rounded-xl overflow-hidden border-2 border-yellow-500">
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                screenshotQuality={0.8}
                className="w-full"
                videoConstraints={{
                  facingMode: 'environment',
                  width: 1280,
                  height: 720
                }}
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowCameraModal(false); setPendingCreditData(null); }}
                className="flex-1 border-gray-500"
              >
                Annuler
              </Button>
              <Button
                onClick={capturePhoto}
                disabled={isLoading}
                className="flex-1"
                style={{ background: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)', color: '#000' }}
              >
                {isLoading ? '...' : '📸 Capturer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Signature */}
      <Dialog open={showSignatureModal} onOpenChange={setShowSignatureModal}>
        <DialogContent className="sm:max-w-xl bg-black/95 border-2 border-red-500 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white text-center">
              ✍️ Reconnaissance de Dette
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-center text-red-400 text-sm">
              Complétez les informations et faites signer le client
            </p>

            <div>
              <Label className="text-white">Adresse du débiteur *</Label>
              <Input
                placeholder="Ex: 12 Rue de la Paix, 62000 Arras"
                value={debtorAddress}
                onChange={(e) => setDebtorAddress(e.target.value)}
                className="mt-2 bg-black/50 border-red-500 text-white"
              />
            </div>

            <div>
              <Label className="text-white">Conditions de remboursement</Label>
              <Input
                placeholder="Ex: En 3 versements mensuels"
                value={repaymentConditions}
                onChange={(e) => setRepaymentConditions(e.target.value)}
                className="mt-2 bg-black/50 border-gray-600 text-white"
              />
            </div>

            <div>
              <Label className="text-white">Date limite de remboursement</Label>
              <Input
                type="date"
                value={repaymentDeadline}
                onChange={(e) => setRepaymentDeadline(e.target.value)}
                className="mt-2 bg-black/50 border-gray-600 text-white"
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">Signature du débiteur *</Label>
              <div className="border-2 border-red-500 rounded-xl bg-white">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    className: 'w-full h-40 rounded-xl'
                  }}
                  backgroundColor="white"
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => signatureRef.current?.clear()}
                className="text-gray-400 mt-1"
              >
                Effacer la signature
              </Button>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowSignatureModal(false); setSignatureClientId(null); }}
                className="flex-1 border-gray-500"
              >
                Annuler
              </Button>
              <Button
                onClick={handleSignature}
                disabled={isLoading}
                className="flex-1"
                style={{ background: 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)' }}
              >
                {isLoading ? '...' : '✅ Valider et générer PDF'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Versement */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="sm:max-w-md bg-black/95 border-2 border-green-500">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white text-center">
              💵 Enregistrer un Versement
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-gray-400">Solde actuel de {selectedClient?.name}</p>
              <p className="text-3xl font-bold text-red-400">{selectedClient?.currentBalance?.toFixed(2)}€</p>
            </div>

            <div>
              <Label className="text-white">Montant du versement (€) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={selectedClient?.currentBalance}
                placeholder="Ex: 50.00"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="mt-2 bg-black/50 border-green-500 text-white text-xl font-bold text-center"
              />
            </div>

            <div>
              <Label className="text-white">Note (optionnel)</Label>
              <Input
                placeholder="Ex: Espèces"
                value={paymentDescription}
                onChange={(e) => setPaymentDescription(e.target.value)}
                className="mt-2 bg-black/50 border-gray-600 text-white"
              />
            </div>

            {paymentAmount && (
              <div className="text-center p-3 bg-green-500/20 rounded-xl border border-green-500">
                <p className="text-sm text-gray-400">Nouveau solde après versement</p>
                <p className="text-2xl font-bold text-green-400">
                  {Math.max(0, (selectedClient?.currentBalance || 0) - parseFloat(paymentAmount || 0)).toFixed(2)}€
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowPaymentModal(false); setPaymentAmount(''); setPaymentDescription(''); }}
                className="flex-1 border-gray-500"
              >
                Annuler
              </Button>
              <Button
                onClick={handlePayment}
                disabled={isLoading || !paymentAmount || parseFloat(paymentAmount) <= 0}
                className="flex-1"
                style={{ background: 'linear-gradient(135deg, #00FF00 0%, #00CC00 100%)', color: '#000' }}
              >
                {isLoading ? '...' : '✅ Enregistrer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Preview PDF Reconnaissance de Dette */}
      <Dialog open={showPdfPreview} onOpenChange={setShowPdfPreview}>
        <DialogContent className="sm:max-w-2xl bg-white border-2 border-gray-300 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-black text-center">
              📄 Reconnaissance de Dette
            </DialogTitle>
          </DialogHeader>
          
          {pdfData && (
            <>
              {/* Contenu PDF */}
              <div ref={pdfRef} className="p-8 bg-white text-black" style={{ fontFamily: 'serif' }}>
                <div className="text-center mb-8">
                  <h1 className="text-2xl font-bold uppercase tracking-wide">Reconnaissance de Dette</h1>
                  <div className="w-32 h-1 bg-black mx-auto mt-2" />
                </div>

                <div className="mb-6">
                  <h2 className="font-bold mb-2">CRÉANCIER :</h2>
                  <p className="ml-4">{CREDITOR_INFO.name}</p>
                  <p className="ml-4">Appartient à : {CREDITOR_INFO.owner}</p>
                  <p className="ml-4">Siège social : {CREDITOR_INFO.address}</p>
                  <p className="ml-4">Numéro SIRET : {CREDITOR_INFO.siret}</p>
                </div>

                <div className="mb-6">
                  <h2 className="font-bold mb-2">DÉBITEUR :</h2>
                  <p className="ml-4">{pdfData.client.name}</p>
                  <p className="ml-4">Adresse : {pdfData.address}</p>
                  <p className="ml-4">Téléphone : {pdfData.client.phone || '-'}</p>
                </div>

                <div className="mb-6">
                  <p><strong>Montant de la dette :</strong> {pdfData.client.currentBalance?.toFixed(2) || '0.00'}€</p>
                  <p><strong>Date de la transaction :</strong> {pdfData.date}</p>
                  <p><strong>Conditions de remboursement :</strong> {pdfData.conditions}</p>
                  {pdfData.deadline && <p><strong>Date limite de remboursement :</strong> {new Date(pdfData.deadline).toLocaleDateString('fr-FR')}</p>}
                </div>

                <div className="mb-8 p-4 border border-gray-400 rounded">
                  <p className="italic">
                    Je soussigné(e), <strong>{pdfData.client.name}</strong>, reconnais devoir la somme de <strong>{pdfData.client.currentBalance?.toFixed(2) || '0.00'}€</strong> au créancier mentionné ci-dessus. Je m&apos;engage à rembourser cette somme selon les conditions stipulées ci-dessus.
                  </p>
                </div>

                <div className="flex justify-between items-end">
                  <div>
                    <p className="font-bold mb-2">Signature du débiteur :</p>
                    {pdfData.signature && (
                      <img src={pdfData.signature} alt="Signature" className="h-20 border-b border-black" />
                    )}
                  </div>
                  <div className="text-right">
                    <p>Date : {pdfData.date}</p>
                  </div>
                </div>

                {/* Photo ID si disponible */}
                {pdfData.client.idPhotoUrl && (
                  <div className="mt-8 pt-4 border-t border-gray-300">
                    <p className="text-sm text-gray-600 mb-2">Pièce d&apos;identité jointe :</p>
                    <img src={pdfData.client.idPhotoUrl} alt="Pièce d&apos;identité" className="max-h-40 mx-auto border border-gray-400" />
                  </div>
                )}
              </div>

              {/* Bouton téléchargement */}
              <div className="flex gap-3 mt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowPdfPreview(false)}
                  className="flex-1"
                >
                  Fermer
                </Button>
                <Button
                  onClick={generatePDF}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Télécharger PDF
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
